import { useCallback, useEffect, useMemo, useState } from 'react'

import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'
import { parseMetadata } from '../../../apps/professional-app/fa/MeetingSummary/utils'

import { useInteractWithAiAgentMutation, useInteractWithAiAgentStreamingMutation } from '../../../store/ai/aiAgent'
import { useLazyGetAiAgentQuery } from '../../../store/ai/aiAgentModel'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { MeetingPrepExecutionResult, MeetingPrepInteractionResult, MeetingPrepResult } from '../../../models/MeetingPrep'
import { AIAgentResult, AIAgentType } from '../../../models/AIAgent'

import { Citation } from '../AIAgentResultCitations/AIAgentResultCitations'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { addMessage, clearChat, selectLastUserChatMessage, setMessages, updateStreamingMessage } from '../../../store/streamingChat/streamingChat'
import { useStreamingChat } from './useStreamingChat'

interface MeetingPrepProps {
  executeMeetingPrep: (meetingTemplateId: string) => Promise<MeetingPrepExecutionResult>
  getExistingMeetingPrep: () => Promise<AIAgentResult | null>
  shouldPoll?: boolean
}

interface MeetingPrepAgentContext {
  meetingTemplateId?: string
}

export interface MeetingPrepEmailEditInteractionPayload {
  type: 'emailEdit'
  email: string
}

export interface MeetingPrepDocumentEditInteractionPayload {
  type: 'documentEdit'
  document: string
}

export type MeetingPrepInteractionPayload = MeetingPrepEmailEditInteractionPayload | MeetingPrepDocumentEditInteractionPayload

export const useMeetingPrep = ({ executeMeetingPrep, getExistingMeetingPrep, shouldPoll = false }: MeetingPrepProps) => {
  const { hasQuota, getQuotaValue } = useZocksQuota()
  const accountId = useAppSelector(selectAccountId)
  const lastUserInteraction = useAppSelector(selectLastUserChatMessage)
  const dispatch = useAppDispatch()

  const [triggerGetAiAgent] = useLazyGetAiAgentQuery()
  const [triggerInteractWithAiAgent] = useInteractWithAiAgentMutation()
  const [triggerInteractWithAiAgentStreaming] = useInteractWithAiAgentStreamingMutation()

  const [isLoading, setLoading] = useState<boolean>(true)
  const [interactionIsLoading, setInteractionIsLoading] = useState<boolean>(false)
  const [editIsLoading, setEditIsLoading] = useState<boolean>(false)
  const [pollingIsLoading, setPollingIsLoading] = useState<boolean>(false)
  const [citationIsLoading, setCitationIsLoading] = useState<boolean>(false)

  const [aiAgentId, setAiAgentId] = useState<string | null>(null)
  const [aiAgentContext, setAiAgentContext] = useState<MeetingPrepAgentContext | null>(null)
  const [existingMeetingPrep, setExistingMeetingPrep] = useState<AIAgentResult | null>(null)
  const [meetingPrep, setMeetingPrep] = useState<MeetingPrepResult | null>(null)
  const [citations, setCitations] = useState<Citation[] | null>(null)

  const autoGenerateCitations = hasQuota('ai.autoGenerateCitations.value')
  const generateCitationsForAgents = getQuotaValue('ai.generateCitationsForAgents.value')
  const citationEnabled = useMemo(
    () => autoGenerateCitations && generateCitationsForAgents.includes(AIAgentType.MEETING_PREPARATION),
    [autoGenerateCitations, generateCitationsForAgents],
  )

  const parseAgentMeetingPrepResult = useCallback(
    (meetingPrepResult: MeetingPrepExecutionResult | MeetingPrepInteractionResult) => {
      const { result } = meetingPrepResult
      setMeetingPrep((prevState) => ({ ...prevState, ...result }))
      const { message } = result
      if (message) {
        dispatch(addMessage({ type: 'agent', message }))
      }
    },
    [dispatch],
  )

  const parseExistingMeetingPrep = useCallback(
    (aiAgentResult: AIAgentResult) => {
      const { originalAiAgentId, result, interaction, metadata } = aiAgentResult
      const { aiAgentId, interactions = [] } = interaction ?? {}
      setAiAgentId(aiAgentId ?? originalAiAgentId)
      setMeetingPrep((prevState) => ({ ...prevState, ...(result as MeetingPrepResult) }))
      dispatch(setMessages(interactions.map((i) => i.result)))
      setLoading(false)
      const { citations, startedGenerating } = parseMetadata(metadata)
      if (citations) {
        setCitations(citations)
        return
      }
      if (startedGenerating) {
        setCitationIsLoading(true)
      }
    },
    [dispatch],
  )

  const onMeetingPrepExecution = useCallback(
    async (selectedMeetingTemplateId: string) => {
      setLoading(true)
      dispatch(clearChat())
      setCitationIsLoading(false)
      setCitations(null)
      try {
        await executeMeetingPrep(selectedMeetingTemplateId)
        setPollingIsLoading(true)
      } finally {
        setLoading(false)
      }
    },
    [dispatch, executeMeetingPrep],
  )

  const onTextDelta = useCallback(
    (data: MeetingPrepResult) => {
      setMeetingPrep((prevState) => ({ ...prevState, ...data }))
      const { message } = data
      if (message) {
        dispatch(updateStreamingMessage(message))
      }
    },
    [dispatch],
  )

  const onNewAiAgentId = useCallback((newAiAgentId: string) => {
    setAiAgentId(newAiAgentId)
  }, [])

  const { onStreamingEvent } = useStreamingChat<MeetingPrepResult>({ onTextDelta, onNewAiAgentId })

  const onMeetingPrepChatInteraction = useCallback(
    async (chatMessage: string) => {
      if (!aiAgentId) {
        return
      }
      setInteractionIsLoading(true)
      try {
        dispatch(addMessage({ type: 'user', message: chatMessage }))
        await triggerInteractWithAiAgentStreaming({
          accountId,
          aiAgentId,
          payload: { type: 'chat', query: chatMessage },
          onStreamingEvent,
        })
      } catch (e) {
        console.error(e)
      } finally {
        setInteractionIsLoading(false)
      }
    },
    [accountId, aiAgentId, dispatch, onStreamingEvent, triggerInteractWithAiAgentStreaming],
  )

  const onMeetingPrepEditInteraction = useCallback(
    async (payload: MeetingPrepInteractionPayload) => {
      if (!aiAgentId) {
        return
      }
      setEditIsLoading(true)
      try {
        const meetingPrepInteractionResult = (await triggerInteractWithAiAgent({
          accountId,
          aiAgentId,
          payload,
        }).unwrap()) as MeetingPrepInteractionResult
        const { newAiAgentId } = meetingPrepInteractionResult
        if (newAiAgentId) {
          setAiAgentId(newAiAgentId)
        }
        parseAgentMeetingPrepResult(meetingPrepInteractionResult)
        return meetingPrepInteractionResult
      } catch (e) {
        console.error(e)
      } finally {
        setEditIsLoading(false)
      }
    },
    [accountId, aiAgentId, parseAgentMeetingPrepResult, triggerInteractWithAiAgent],
  )

  const getMeetingPrep = useCallback(async () => {
    const aiAgentResult = await getExistingMeetingPrep()
    if (!aiAgentResult) {
      setPollingIsLoading(false)
      return
    }

    const { citations, emailSignatureProcessed, startedGenerating } = parseMetadata(aiAgentResult.metadata)
    // startedGenerating can overwrite emailSignatureProcessed because of metadata handling so here we will temporarily
    // check for both to make sure we don't exit polling too early
    if (!emailSignatureProcessed && !startedGenerating) {
      return
    }
    parseExistingMeetingPrep(aiAgentResult)
    setPollingIsLoading(false)
    if (!citations && citationEnabled) {
      setCitationIsLoading(true)
    }
  }, [getExistingMeetingPrep, citationEnabled, parseExistingMeetingPrep])

  const { start: startPollingMeetingPrep, stop: stopPollingMeetingPrep } = usePollingEffect(getMeetingPrep, [], {
    interval: 5000,
  })

  const handleResubmitInteraction = useCallback(async () => {
    if (!aiAgentId || !lastUserInteraction) {
      return
    }
    setInteractionIsLoading(true)
    try {
      await triggerInteractWithAiAgentStreaming({
        accountId,
        aiAgentId,
        payload: { type: 'chat', query: lastUserInteraction.message },
        onStreamingEvent,
      })
    } catch (e) {
      console.error(e)
    } finally {
      setInteractionIsLoading(false)
    }
  }, [accountId, aiAgentId, lastUserInteraction, onStreamingEvent, triggerInteractWithAiAgentStreaming])

  useEffect(() => {
    if (shouldPoll || pollingIsLoading) {
      startPollingMeetingPrep()
    } else {
      stopPollingMeetingPrep()
    }
  }, [shouldPoll, pollingIsLoading, startPollingMeetingPrep, stopPollingMeetingPrep])

  const getCitations = useCallback(async () => {
    const aiAgentResult = await getExistingMeetingPrep()
    if (!aiAgentResult) {
      setCitationIsLoading(false)
      return
    }
    const { citations } = parseMetadata(aiAgentResult.metadata)
    if (!citations) {
      return
    }
    parseExistingMeetingPrep(aiAgentResult)
    setCitationIsLoading(false)
  }, [getExistingMeetingPrep, parseExistingMeetingPrep])

  const { start: startPollingCitations, stop: stopPollingCitations } = usePollingEffect(getCitations, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (citationIsLoading) {
      startPollingCitations()
    } else {
      stopPollingCitations()
    }
  }, [citationIsLoading, startPollingCitations, stopPollingCitations])

  useEffect(() => {
    const getMeetingPrep = async () => {
      const aiAgentResult = await getExistingMeetingPrep()
      if (aiAgentResult) {
        setExistingMeetingPrep(aiAgentResult)
        parseExistingMeetingPrep(aiAgentResult)
      } else {
        setLoading(false)
      }
    }
    getMeetingPrep()
  }, [getExistingMeetingPrep, parseExistingMeetingPrep])

  useEffect(() => {
    if (!aiAgentId) {
      return
    }
    const getAiAgentContext = async () => {
      try {
        const aiAgent = await triggerGetAiAgent({
          accountId,
          aiAgentId,
        }).unwrap()
        if (!aiAgent) {
          return
        }
        setAiAgentContext(aiAgent.context)
      } catch (e) {
        console.error(e)
      }
    }
    getAiAgentContext()
  }, [accountId, aiAgentId, triggerGetAiAgent])

  return {
    meetingPrep,
    existingMeetingPrep,
    isLoading: isLoading || pollingIsLoading || shouldPoll,
    onMeetingPrepExecution,
    interactionIsLoading,
    editIsLoading,
    onMeetingPrepChatInteraction,
    onMeetingPrepEditInteraction,
    handleResubmitInteraction,
    aiAgentContext,
    citations,
    citationIsLoading,
  }
}
